import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  MessagesSquare,
  Plus,
  Search,
  RefreshCw,
  Pin,
  Lock,
  Users,
  Loader2,
} from "lucide-react";
import {
  useForumThreadsQuery,
  useForumAccessQuery,
  type ForumThreadSummary,
} from "../api/forumApi";

/** "3 hours ago" — a forum index reads by recency, not by calendar date. */
function ago(iso: string | null) {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  const mins = Math.round((Date.now() - then) / 60000);

  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} hr${hours === 1 ? "" : "s"} ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days} day${days === 1 ? "" : "s"} ago`;
  return new Date(iso).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function ThreadRow({ thread, onOpen }: { thread: ForumThreadSummary; onOpen: () => void }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className="w-full text-left px-5 py-4 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0"
    >
      <div className="flex items-start gap-3">
        {/* The unread dot is the whole point of the read table — it is what
            makes this usable for someone who signs in once a week. */}
        <span
          className={`mt-2 w-2 h-2 rounded-full shrink-0 ${
            thread.is_unread ? "bg-indigo-500" : "bg-transparent"
          }`}
        />

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            {thread.is_pinned && (
              <Pin className="w-3.5 h-3.5 text-amber-500 shrink-0" />
            )}
            {thread.is_locked && (
              <Lock className="w-3.5 h-3.5 text-gray-400 shrink-0" />
            )}
            <span
              className={`text-sm tracking-tight truncate ${
                thread.is_unread ? "font-black text-gray-900" : "font-bold text-gray-700"
              }`}
            >
              {thread.title}
            </span>
          </div>

          <p className="text-xs text-gray-500 mt-1 line-clamp-2 leading-relaxed">
            {thread.excerpt}
          </p>

          <div className="flex items-center gap-2 mt-2 text-[10px] font-bold text-gray-400 flex-wrap">
            <span className="text-gray-600">{thread.author?.name ?? "Unknown"}</span>
            {thread.author?.seat && (
              <span className="rounded-lg bg-gray-100 px-2 py-0.5 text-gray-500">
                {thread.author.seat}
              </span>
            )}
            <span>·</span>
            <span>{ago(thread.last_activity_at || thread.created_at)}</span>
          </div>
        </div>

        <div className="shrink-0 text-right">
          <div className="text-sm font-black text-gray-900">{thread.replies_count}</div>
          <div className="text-[9px] font-black uppercase tracking-[0.15em] text-gray-400">
            {thread.replies_count === 1 ? "reply" : "replies"}
          </div>
        </div>
      </div>
    </button>
  );
}

export default function ForumThreadsPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const { data: access } = useForumAccessQuery();
  const { data, isLoading, isFetching, refetch } = useForumThreadsQuery({
    q: search || undefined,
    page,
  });

  const threads = data?.data ?? [];

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto">
      {/* ── Header ──────────────────────────────────────────────────── */}
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-2xl bg-gray-900 flex items-center justify-center">
              <MessagesSquare className="w-4 h-4 text-white" />
            </div>
            <h1 className="text-xl font-black text-gray-900 tracking-tight">
              Policy Committee Forum
            </h1>
          </div>
          <p className="text-xs text-gray-500 mt-2 max-w-xl leading-relaxed">
            A closed room for the National Policy Committee. Everything posted
            here is visible to the committee and to no one else.
            {access?.seat && (
              <>
                {" "}
                You are here as <span className="font-bold text-gray-700">{access.seat}</span>.
              </>
            )}
            {access?.allowed && !access?.seat && (
              <> You are viewing this as secretariat.</>
            )}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => navigate("/forum/members")}
            className="inline-flex items-center gap-2 rounded-xl border border-gray-200 px-3 py-2.5 text-[10px] font-black uppercase tracking-[0.15em] text-gray-600 hover:border-gray-300"
          >
            <Users className="w-3.5 h-3.5" />
            Who is here
          </button>
          <button
            type="button"
            onClick={() => navigate("/forum/new")}
            className="inline-flex items-center gap-2 rounded-xl bg-gray-900 px-4 py-2.5 text-[10px] font-black uppercase tracking-[0.15em] text-white hover:bg-gray-800"
          >
            <Plus className="w-3.5 h-3.5" />
            New discussion
          </button>
        </div>
      </div>

      {/* ── Search ──────────────────────────────────────────────────── */}
      <div className="flex items-center gap-2 mb-4">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-gray-300 absolute left-3.5 top-1/2 -translate-y-1/2" />
          <input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="Search discussions…"
            className="w-full rounded-xl border border-gray-200 bg-white pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:border-gray-400"
          />
        </div>
        <button
          type="button"
          onClick={() => refetch()}
          className="rounded-xl border border-gray-200 p-2.5 text-gray-500 hover:border-gray-300"
          title="Refresh"
        >
          <RefreshCw className={`w-4 h-4 ${isFetching ? "animate-spin" : ""}`} />
        </button>
      </div>

      {/* ── List ────────────────────────────────────────────────────── */}
      <div className="rounded-2xl border border-gray-100 bg-white overflow-hidden shadow-sm">
        {isLoading ? (
          <div className="flex items-center justify-center py-20 text-gray-300">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        ) : threads.length === 0 ? (
          <div className="text-center py-20 px-6">
            <p className="text-sm font-bold text-gray-500">
              {search ? "Nothing matches that search." : "No discussions yet."}
            </p>
            {!search && (
              <p className="text-xs text-gray-400 mt-1.5">
                Start the first one — it will be visible to the committee only.
              </p>
            )}
          </div>
        ) : (
          threads.map((t) => (
            <ThreadRow key={t.id} thread={t} onOpen={() => navigate(`/forum/${t.id}`)} />
          ))
        )}
      </div>

      {/* ── Paging ──────────────────────────────────────────────────── */}
      {(data?.last_page ?? 1) > 1 && (
        <div className="flex items-center justify-between mt-4">
          <span className="text-[10px] font-black uppercase tracking-[0.15em] text-gray-400">
            Page {data?.current_page} of {data?.last_page} · {data?.total} discussions
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={(data?.current_page ?? 1) <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="rounded-xl border border-gray-200 px-3 py-2 text-[10px] font-black uppercase tracking-[0.15em] text-gray-600 disabled:opacity-40"
            >
              Previous
            </button>
            <button
              type="button"
              disabled={(data?.current_page ?? 1) >= (data?.last_page ?? 1)}
              onClick={() => setPage((p) => p + 1)}
              className="rounded-xl border border-gray-200 px-3 py-2 text-[10px] font-black uppercase tracking-[0.15em] text-gray-600 disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
